import React from "react";
import { FmAssetImage } from "./FmAssetImage";
import { FmBanner } from "./FmBanner";
import { FmButton } from "./FmButton";
import { FmControlSurface } from "./FmField";
import { FmRoutePage, FmStack, FmText } from "./FmComposition";
import { FmRiskWorkspace } from "./FmRiskWorkspace";
import { FmSplitSection } from "./FmSplitSection";
import { FmUnlockCard } from "./FmUnlockCard";
import type {
  FmRiskWorkspacePageDescriptor,
  FmRiskWorkspaceRootProps,
} from "./FmRiskWorkspaceRoot.types";
import { validateRiskWorkspacePageDescriptor } from "./FmRiskWorkspaceRoot.validation";
import { FmRiskWorkspaceMapSnapshotProvider } from "@/shared/riskWorkspace/RiskWorkspaceMapSnapshot";

function Notice({ descriptor }: { descriptor: FmRiskWorkspacePageDescriptor }) {
  const notice = descriptor.notice;
  if (!notice) return null;
  return (
    <FmBanner
      tone="info"
      title={notice.title}
      description={notice.description}
      action={
        notice.action ? (
          <FmButton variant="bordered" onClick={notice.action.onSelect}>
            {notice.action.label}
          </FmButton>
        ) : undefined
      }
      data-testid={notice.testId}
    />
  );
}

function StateSurface({
  descriptor,
}: {
  descriptor: Extract<FmRiskWorkspacePageDescriptor, { kind: "surface" }>;
}) {
  return (
    <FmControlSurface
      data-testid={descriptor.testId}
      title={descriptor.title}
      description={descriptor.description}
      width="content"
      actions={
        descriptor.action ? (
          <FmButton variant="bordered" onClick={descriptor.action.onSelect}>
            {descriptor.action.label}
          </FmButton>
        ) : undefined
      }
    >
      <FmText tone={descriptor.state === "empty" ? "subtle" : "muted"}>
        {descriptor.body}
      </FmText>
    </FmControlSurface>
  );
}

function LockedPreview({
  descriptor,
}: {
  descriptor: Extract<
    FmRiskWorkspacePageDescriptor,
    { kind: "locked-preview" }
  >;
}) {
  return (
    <div data-testid="job-unlock-preview" data-job="watch">
      <FmSplitSection
        side="start"
        eyebrow={descriptor.eyebrow}
        title={descriptor.title}
        body={descriptor.body}
        bullets={descriptor.bullets}
        graphic={
          <FmAssetImage
            assetKey={descriptor.assetKey}
            altOverride={descriptor.title}
          />
        }
        actions={
          <FmStack gap="sm">
            <FmBanner
              tone="info"
              title={descriptor.sampleTitle}
              description={descriptor.sampleBody}
              data-testid="sentry-sample-advisory"
            />
            {/* `upgrade-prompt` is the app-wide semantic denial marker (the same
                hook PropertyAccessGuard renders for a blocked tier). Free-tier
                owners on /app/watch reach this real paywall card as their
                terminal denied state; access-control E2Es race this marker
                against `sentry-setup-card` to prove enforcement, so it must ride
                the rendered upgrade card — not a legacy wrapper class. */}
            <div data-testid="upgrade-prompt">
              <FmUnlockCard
                data-testid="sentry-unlock-card"
                title={descriptor.unlock.title}
                unlocksSummary={descriptor.unlock.summary}
                priceLabel={descriptor.unlock.priceLabel}
                ctaLabel={descriptor.unlock.actionLabel}
                ctaTestId="sentry-plot-package-unlock"
                onCtaClick={descriptor.unlock.onSelect}
              />
            </div>
          </FmStack>
        }
        data-testid="sentry-locked-preview"
      />
    </div>
  );
}

function ArmSurface({
  descriptor,
}: {
  descriptor: Extract<FmRiskWorkspacePageDescriptor, { kind: "arm" }>;
}) {
  return (
    <FmControlSurface
      data-testid="sentry-setup-card"
      title={descriptor.title}
      description={descriptor.body}
      width="content"
      actions={
        <FmButton
          variant="primary"
          loading={descriptor.action.loading}
          disabled={descriptor.action.loading}
          onClick={descriptor.action.onSelect}
          data-testid="sentry-start-watching-btn"
        >
          {descriptor.action.label}
        </FmButton>
      }
    >
      <FmStack gap="sm">
        <FmText tone="muted">{descriptor.coverage}</FmText>
        {descriptor.error ? (
          <FmBanner tone="error" title={descriptor.error} />
        ) : null}
      </FmStack>
    </FmControlSurface>
  );
}

function PageState({
  descriptor,
}: {
  descriptor: FmRiskWorkspacePageDescriptor;
}) {
  switch (descriptor.kind) {
    case "surface":
      return <StateSurface descriptor={descriptor} />;
    case "locked-preview":
      return <LockedPreview descriptor={descriptor} />;
    case "arm":
      return <ArmSurface descriptor={descriptor} />;
    case "workspace": {
      const workspace = (
        <FmRiskWorkspace
          {...descriptor.workspace}
          data-testid="sentry-risk-workspace"
        />
      );
      return descriptor.mapSnapshot ? (
        <FmRiskWorkspaceMapSnapshotProvider snapshot={descriptor.mapSnapshot}>
          {workspace}
        </FmRiskWorkspaceMapSnapshotProvider>
      ) : (
        workspace
      );
    }
  }
}

/** Descriptor-only route archetype for risk monitoring and alert evidence. */
export const FmRiskWorkspaceRoot: React.FC<FmRiskWorkspaceRootProps> = ({
  descriptor,
  preview = false,
}) => {
  validateRiskWorkspacePageDescriptor(descriptor);
  const content = (
    <FmStack gap="lg">
      <Notice descriptor={descriptor} />
      <PageState descriptor={descriptor} />
    </FmStack>
  );
  if (preview) return <div data-testid="risk-workspace-preview">{content}</div>;
  return (
    <FmRoutePage
      as="div"
      width="page"
      padY="lg"
      data-testid="sentry-watch-view"
    >
      {content}
    </FmRoutePage>
  );
};

FmRiskWorkspaceRoot.displayName = "FmRiskWorkspaceRoot";
